import { createTheme, alpha } from '@mui/material/styles';

/**
 * Tema Profissional - License Manager
 * Cor primária: #1565c0 (padrão dos projetos)
 * Dark Mode: Base preta
 */

// Paleta azul baseada em #1565c0
const brand = {
    50: '#e3f2fd',
    100: '#bbdefb',
    200: '#90caf9',
    300: '#64b5f6',
    400: '#42a5f5',
    500: '#1565c0',
    600: '#1565c0',
    700: '#0d47a1',
    800: '#0a3d91',
    900: '#062a6e',
};

// Neutros
const slate = {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
};

// Dark mode base
const zinc = {
    50: '#fafafa',
    100: '#f4f4f5',
    200: '#e4e4e7',
    300: '#d4d4d8',
    400: '#a1a1aa',
    500: '#71717a',
    600: '#52525b',
    700: '#3f3f46',
    800: '#27272a',
    900: '#18181b',
    950: '#09090b',
};

export const createAppTheme = (mode: 'light' | 'dark') => {
    const isDark = mode === 'dark';

    // Cores primárias - Azul #1565c0
    const primaryMain = isDark ? brand[400] : brand[500];
    const primaryLight = isDark ? brand[300] : brand[400];
    const primaryDark = isDark ? brand[600] : brand[700];

    // Background - Dark mode uses softer dark for better eye comfort
    const bgDefault = isDark ? zinc[900] : slate[100];
    const bgPaper = isDark ? zinc[800] : '#ffffff';
    const bgElevated = isDark ? zinc[700] : slate[100];

    // Texto
    const textPrimary = isDark ? zinc[50] : slate[900];
    const textSecondary = isDark ? zinc[400] : slate[600];
    const textMuted = isDark ? zinc[500] : slate[400];

    // Bordas - Increased opacity for better visibility
    const borderColor = isDark ? alpha(zinc[100], 0.12) : alpha(slate[900], 0.12);
    const borderColorStrong = isDark ? alpha(zinc[100], 0.20) : alpha(slate[900], 0.20);

    return createTheme({
        palette: {
            mode,
            primary: {
                main: primaryMain,
                light: primaryLight,
                dark: primaryDark,
                contrastText: '#ffffff',
            },
            secondary: {
                main: isDark ? slate[400] : slate[600],
                light: isDark ? slate[300] : slate[500],
                dark: isDark ? slate[600] : slate[700],
                contrastText: '#ffffff',
            },
            background: {
                default: bgDefault,
                paper: bgPaper,
            },
            text: {
                primary: textPrimary,
                secondary: textSecondary,
            },
            divider: borderColor,
            error: {
                main: isDark ? '#f87171' : '#dc2626',
                light: '#fca5a5',
                dark: '#b91c1c',
            },
            warning: {
                main: isDark ? '#fbbf24' : '#d97706',
                light: '#fcd34d',
                dark: '#b45309',
            },
            success: {
                main: isDark ? '#4ade80' : '#16a34a',
                light: '#86efac',
                dark: '#15803d',
            },
            info: {
                main: isDark ? '#60a5fa' : '#2563eb',
                light: '#93c5fd',
                dark: '#1d4ed8',
            },
            grey: zinc,
            action: {
                hover: isDark ? alpha('#ffffff', 0.08) : alpha('#000000', 0.04),
                selected: isDark ? alpha(primaryMain, 0.15) : alpha(primaryMain, 0.08),
                disabled: textMuted,
                disabledBackground: isDark ? zinc[700] : slate[200],
            },
        },
        typography: {
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            h1: { fontWeight: 600, letterSpacing: '-0.025em', fontSize: '2.25rem' },
            h2: { fontWeight: 600, letterSpacing: '-0.025em', fontSize: '1.875rem' },
            h3: { fontWeight: 600, letterSpacing: '-0.01em', fontSize: '1.5rem' },
            h4: { fontWeight: 600, fontSize: '1.25rem' },
            h5: { fontWeight: 600, fontSize: '1.125rem' },
            h6: { fontWeight: 600, fontSize: '1rem' },
            subtitle1: { fontWeight: 500, color: textSecondary },
            subtitle2: { fontWeight: 500, fontSize: '0.875rem', color: textSecondary },
            body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
            body2: { fontSize: '0.875rem', lineHeight: 1.6 },
            button: { fontWeight: 500, textTransform: 'none', letterSpacing: '0.01em' },
            caption: { fontSize: '0.75rem', color: textMuted },
        },
        shape: { borderRadius: 6 },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        scrollbarColor: isDark ? `${zinc[600]} ${zinc[800]}` : `${slate[300]} ${slate[100]}`,
                        '&::-webkit-scrollbar, & *::-webkit-scrollbar': { width: 10, height: 10 },
                        '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
                            borderRadius: 5,
                            backgroundColor: isDark ? zinc[600] : slate[300],
                            border: isDark ? `2px solid ${zinc[800]}` : `2px solid ${slate[100]}`,
                        },
                        '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
                            backgroundColor: 'transparent',
                        },
                        '::selection': { backgroundColor: alpha(primaryMain, 0.3) },
                    },
                },
            },
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: {
                        borderRadius: 6,
                        padding: '10px 20px',
                        fontSize: '0.875rem',
                        transition: 'all 0.15s ease',
                    },
                    contained: {
                        '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(primaryMain, 0.3)}`,
                        },
                    },
                    containedPrimary: {
                        background: `linear-gradient(135deg, ${brand[500]} 0%, ${brand[600]} 100%)`,
                        '&:hover': {
                            background: `linear-gradient(135deg, ${brand[400]} 0%, ${brand[500]} 100%)`,
                        },
                    },
                    outlined: {
                        borderWidth: 1.5,
                        borderColor: borderColorStrong,
                        '&:hover': { borderWidth: 1.5, backgroundColor: isDark ? alpha('#ffffff', 0.03) : alpha('#000000', 0.02) },
                    },
                    outlinedPrimary: {
                        borderColor: brand[500],
                        '&:hover': { borderColor: brand[400], backgroundColor: alpha(primaryMain, 0.08) },
                    },
                    sizeSmall: { padding: '6px 14px', fontSize: '0.8125rem' },
                    sizeLarge: { padding: '14px 28px', fontSize: '0.9375rem' },
                },
            },
            MuiCard: {
                defaultProps: { elevation: 0 },
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        backgroundColor: bgPaper,
                        border: `1px solid ${borderColor}`,
                        transition: 'border-color 0.15s ease',
                        '&:hover': { borderColor: borderColorStrong },
                    },
                },
            },
            MuiCardContent: {
                styleOverrides: {
                    root: { padding: 24, '&:last-child': { paddingBottom: 24 } },
                },
            },
            MuiPaper: {
                defaultProps: { elevation: 0 },
                styleOverrides: {
                    root: { backgroundImage: 'none' },
                    rounded: { borderRadius: 8 },
                    outlined: { borderColor: borderColor },
                },
            },
            MuiAppBar: {
                defaultProps: { elevation: 0 },
                styleOverrides: {
                    root: {
                        backgroundColor: isDark ? alpha(zinc[900], 0.85) : alpha('#ffffff', 0.8),
                        backdropFilter: 'blur(12px)',
                        borderBottom: `1px solid ${borderColor}`,
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: { backgroundColor: bgPaper, borderRight: `1px solid ${borderColor}` },
                },
            },
            MuiTableContainer: {
                styleOverrides: {
                    root: { borderRadius: 8, border: `1px solid ${borderColor}`, overflow: 'hidden' },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: { borderBottom: `1px solid ${borderColor}`, padding: '18px 20px' },
                    head: {
                        fontWeight: 600,
                        backgroundColor: bgElevated,
                        color: textSecondary,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                    },
                },
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        transition: 'background-color 0.1s ease',
                        '&:hover': { backgroundColor: isDark ? alpha('#ffffff', 0.02) : alpha('#000000', 0.02) },
                        '&:last-child td': { borderBottom: 'none' },
                    },
                },
            },
            MuiTextField: {
                defaultProps: { variant: 'outlined', size: 'small' },
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                        borderRadius: 4,
                            backgroundColor: isDark ? alpha('#ffffff', 0.02) : alpha('#000000', 0.02),
                            '&:hover': { backgroundColor: isDark ? alpha('#ffffff', 0.04) : alpha('#000000', 0.03) },
                            '&.Mui-focused': {
                                backgroundColor: 'transparent',
                                '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2, borderColor: primaryMain },
                            },
                        },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: borderColor },
                        '& .MuiInputLabel-root': { color: textSecondary },
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: { borderRadius: 6 },
                    notchedOutline: { borderColor: borderColor },
                },
            },
            MuiSelect: {
                styleOverrides: { root: { borderRadius: 6 } },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: 10,
                        backgroundColor: bgPaper,
                        border: `1px solid ${borderColor}`,
                        boxShadow: isDark ? '0 25px 50px -12px rgba(0, 0, 0, 0.8)' : '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    },
                },
            },
            MuiDialogTitle: {
                styleOverrides: { root: { fontSize: '1.25rem', fontWeight: 600, padding: '24px 24px 16px' } },
            },
            MuiDialogContent: {
                styleOverrides: { root: { padding: '16px 24px' } },
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: {
                        padding: '16px 24px 24px',
                        gap: 12,
                        '& > :not(style) ~ :not(style)': { marginLeft: 0 },
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: { borderRadius: 4, fontWeight: 500, fontSize: '0.75rem' },
                    filled: { backgroundColor: isDark ? zinc[800] : slate[100] },
                    outlined: { borderColor: borderColorStrong },
                    colorPrimary: {
                        backgroundColor: isDark ? alpha(primaryMain, 0.15) : alpha(primaryMain, 0.25),
                        color: isDark ? alpha(primaryMain, 0.9) : primaryDark,
                        border: `1px solid ${isDark ? alpha(primaryMain, 0.3) : alpha(primaryMain, 0.3)}`,
                    },
                    colorSuccess: {
                        backgroundColor: isDark ? alpha('#4ade80', 0.15) : alpha('#16a34a', 0.25),
                        color: isDark ? '#86efac' : '#14532d',
                        border: `1px solid ${isDark ? alpha('#4ade80', 0.3) : alpha('#16a34a', 0.3)}`,
                    },
                    colorError: {
                        backgroundColor: isDark ? alpha('#f87171', 0.15) : alpha('#dc2626', 0.25),
                        color: isDark ? '#fca5a5' : '#7f1d1d',
                        border: `1px solid ${isDark ? alpha('#f87171', 0.3) : alpha('#dc2626', 0.3)}`,
                    },
                    colorWarning: {
                        backgroundColor: isDark ? alpha('#fbbf24', 0.15) : alpha('#d97706', 0.25),
                        color: isDark ? '#fcd34d' : '#78350f',
                        border: `1px solid ${isDark ? alpha('#fbbf24', 0.3) : alpha('#d97706', 0.3)}`,
                    },
                    colorInfo: {
                        backgroundColor: isDark ? alpha('#60a5fa', 0.15) : alpha('#2563eb', 0.25),
                        color: isDark ? '#93c5fd' : '#1e3a8a',
                        border: `1px solid ${isDark ? alpha('#60a5fa', 0.3) : alpha('#2563eb', 0.3)}`,
                    },
                },
            },
            MuiAlert: {
                styleOverrides: {
                    root: { borderRadius: 6, border: '1px solid' },
                    standardSuccess: {
                        backgroundColor: isDark ? alpha('#4ade80', 0.1) : alpha('#16a34a', 0.08),
                        borderColor: alpha('#4ade80', 0.3),
                        color: isDark ? '#86efac' : '#166534',
                    },
                    standardError: {
                        backgroundColor: isDark ? alpha('#f87171', 0.1) : alpha('#dc2626', 0.08),
                        borderColor: isDark ? alpha('#f87171', 0.3) : alpha('#dc2626', 0.3),
                        color: isDark ? '#fca5a5' : '#991b1b',
                    },
                    standardWarning: {
                        backgroundColor: isDark ? alpha('#fbbf24', 0.1) : alpha('#d97706', 0.08),
                        borderColor: isDark ? alpha('#fbbf24', 0.3) : alpha('#d97706', 0.3),
                        color: isDark ? '#fcd34d' : '#92400e',
                    },
                    standardInfo: {
                        backgroundColor: isDark ? alpha('#60a5fa', 0.1) : alpha('#2563eb', 0.08),
                        borderColor: isDark ? alpha('#60a5fa', 0.3) : alpha('#2563eb', 0.3),
                        color: isDark ? '#93c5fd' : '#1e40af',
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        backgroundColor: isDark ? zinc[700] : slate[800],
                        color: '#ffffff',
                        borderRadius: 6,
                        fontSize: '0.75rem',
                        padding: '8px 12px',
                    },
                    arrow: { color: isDark ? zinc[700] : slate[800] },
                },
            },
            MuiMenu: {
                styleOverrides: {
                    paper: {
                        borderRadius: 6,
                        backgroundColor: bgPaper,
                        border: `1px solid ${borderColor}`,
                        boxShadow: isDark ? '0 10px 40px rgba(0, 0, 0, 0.5)' : '0 10px 40px rgba(0, 0, 0, 0.15)',
                        marginTop: 4,
                    },
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        borderRadius: 4,
                        margin: '4px 6px',
                        padding: '10px 14px',
                        fontSize: '0.875rem',
                        '&:hover': { backgroundColor: isDark ? alpha('#ffffff', 0.05) : alpha('#000000', 0.04) },
                        '&.Mui-selected': {
                            backgroundColor: alpha(primaryMain, 0.1),
                            '&:hover': { backgroundColor: alpha(primaryMain, 0.15) },
                        },
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 6,
                        '&:hover': { backgroundColor: isDark ? alpha('#ffffff', 0.08) : alpha('#000000', 0.06) },
                    },
                },
            },
            MuiTabs: {
                styleOverrides: {
                    root: { minHeight: 44 },
                    indicator: { height: 3, borderRadius: '3px 3px 0 0' },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        minHeight: 44,
                        padding: '12px 20px',
                        '&:hover': { color: primaryMain, backgroundColor: alpha(primaryMain, 0.05) },
                        '&.Mui-selected': { fontWeight: 600 },
                    },
                },
            },
            MuiListItemButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 6,
                        margin: '2px 8px',
                        padding: '10px 16px',
                        '&:hover': { backgroundColor: isDark ? alpha('#ffffff', 0.05) : alpha('#000000', 0.04) },
                        '&.Mui-selected': {
                            backgroundColor: alpha(primaryMain, 0.1),
                            '&:hover': { backgroundColor: alpha(primaryMain, 0.15) },
                        },
                    },
                },
            },
            MuiLinearProgress: {
                styleOverrides: {
                    root: { borderRadius: 4, backgroundColor: isDark ? zinc[800] : slate[200] },
                    bar: { borderRadius: 4 },
                },
            },
            MuiSwitch: {
                styleOverrides: {
                    root: { width: 44, height: 24, padding: 0 },
                    switchBase: {
                        padding: 2,
                        '&.Mui-checked': {
                            transform: 'translateX(20px)',
                            color: '#fff',
                            '& + .MuiSwitch-track': { backgroundColor: primaryMain, opacity: 1 },
                        },
                    },
                    thumb: { width: 20, height: 20, boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)' },
                    track: { borderRadius: 12, backgroundColor: isDark ? zinc[600] : slate[300], opacity: 1 },
                },
            },
            MuiSkeleton: {
                styleOverrides: {
                    root: { backgroundColor: isDark ? zinc[800] : slate[200] },
                },
            },
            MuiDivider: {
                styleOverrides: { root: { borderColor: borderColor } },
            },
        },
    });
};
